<template>
	<!-- generateStyle() permet de générer les variables Css dynamique utilisable dans la section <style></style> -->
	<b-sidebar :visible="DisplayWidgetsLib" title="$t('NewWidget')" :right="Right" shadow 
		sidebar-class="bg-white" header-class="bg-white px-0"
		:width="checkSmPortrait() ? '23vw' : '320px'"
		:style="generateStyle()">
		<!-- Header de la sidebar -->
		<template #header="{ hide }">
			<b-row class="w-100 p-0 m-0">
				<b-col v-if="!checkSmPortrait()" cols="9" class="lead font-weight-semibold">
					{{ $t('NewWidget') }}
				</b-col>
				<b-col :cols="checkSmPortrait() ? 12 : 3" class="text-right text-gray-dark d-flex justify-content-end align-items-center">
					<b-icon @click="hide" role="button" icon="x-lg" font-scale="0.6" v-b-tooltip.hover :title="$t('Close')" />
				</b-col>
			</b-row>
		</template>

		<!-- Evènements @touchstart, @touchmove et @touchend pour gestion ajout de widgets sur écrans tactiles -->
		<b-row :id="'row-' + $id(widgetLibrary.Name)" v-for="widgetLibrary in widgetsLibrary(DashboardType, ObjectType)" :key="widgetLibrary.Name"
			draggable="true"
			v-on="checkSmPortrait() ? { click: () => openToolTip(widgetLibrary.Name) } : undefined"
			@dblclick="selectWidgetLibrary(widgetLibrary.Name, true)"
			@dragstart.stop="selectWidgetLibrary(widgetLibrary.Name)" @dragover.prevent @drop.prevent
			@drag.stop="$emit('DragEvent');"
			@dragend.stop="$emit('DragEndEvent');"
			@touchstart.stop="selectWidgetLibrary(widgetLibrary.Name)"
			@touchmove.stop="$emit('DragEvent');"
			@touchend.stop="$emit('DragEndEvent');"
			:class="['WidgetLibrary m-2 rounded', { disabled: widgetLibrary.Disabled }]">
			<!-- Icone du widget -->
			<b-col :cols="!checkSmPortrait() ? 'auto' : 12" :order="Right ? 1 : 2" :class="['d-flex justify-content-center', { 'py-2': checkSmPortrait() }]" align-self="center">
				<div :class="['WidgetIcon d-flex align-content-center justify-content-center flex-wrap', { disabled: widgetLibrary.Disabled }]">
					<font-awesome-icon v-if="widgetLibrary.Icon" :icon="widgetLibrary.Icon" size="lg" />
					<div v-else>{{ widgetLibrary.Label }}</div>
				</div>
			</b-col>
			<!-- Infobulle pour le mode smartphone affichable sur un clic -->
			<b-tooltip :disabled="!checkSmPortrait()" :ref="'tooltip' + widgetLibrary.Name" :target="'row-' + $id(widgetLibrary.Name)">
				<div class="lead">{{ widgetLibrary.Label }}</div>
				<p>{{ widgetLibrary.Description }}</p>
			</b-tooltip>
			<!-- Nom et description du widget -->
			<b-col v-if="!checkSmPortrait()"
				 cols="9" :order="Right ? 2 : 1" :class="['pt-2 Description', { disabled: widgetLibrary.Disabled }, { 'pl-0': Right }]">
				<div class="lead">{{ widgetLibrary.Label }}</div>
				<p class="text-truncate">{{ widgetLibrary.Description }}</p>
			</b-col>
		</b-row>
	</b-sidebar>
</template>

<script lang="ts">
	import { InjectReactive, Model, Prop } from 'vue-property-decorator';
	import Component, { mixins } from 'vue-class-component';
	import { Getter } from 'vuex-class';
	import DashboardTypeEnum from '@/enums/DashboardTypeEnum';
	import tWidgetLibrary from '@/types/Dashboard/WidgetLibrary';
	import { ScreenUtils, ThemeUtils } from "@/components/commons/Utils";
	import locales from './locales/WidgetLibrary.json';

	/** Composant d'affichage de la bibliothèque de widgets */
	@Component({
		i18n : { messages : locales }
	})
	export default class WidgetLibrary extends mixins(ScreenUtils, ThemeUtils) {
		/** Affichage ou non de la bibliothèque de widgets */
		@Prop()
		public DisplayWidgetsLib!: boolean;

		/** Indique si la Sidebar de bibliothèque  est affichée à droite ou gauche */
		@Prop({ default: () => false })
		public Right!: boolean;

		//#region Gestion style dynamique
		/** Variant de couleur à utiliser sur le tableau de bord */
		@InjectReactive()
		public Variant!: string;

		/** Indique à quelle hauteur se positionne la sidebar */
		@Prop()
		public Top!: string;

		/** 
		 * Génère les variables CSS à injecter dans la section style
		 * @description Calcul de variable pour injecter le css pour gérer le top de la Sidebar. Utilisé dans la section style dans :deep(.b-sidebar)
		 * @todo Bug dans Vue.js 2.7 pour utiliser le binding direct de variable dans la section Style. A faire retenter lors du passage en Vue.js 3.
		 * @see https://stackoverflow.com/questions/46551925/vuejs-v-bindstyle-hover
		 * @see https://vuejs.org/api/sfc-css-features.html#v-bind-in-css
		 * @see https://github.com/vuejs/vue/issues/12621
		 */
		private generateStyle() {
			return { '--SideBarTop': this.Top, ...this.generateVariantStyle(this.Variant)};
		}
		//#endregion Gestion style dynamique

		/** Type de dashboard pour lequel la bibliothèque de widgets doit être chargée */
		@Prop()
		public DashboardType!: DashboardTypeEnum;

		/** Type d'objet sur lequel filtré (à partir de la propriété targetObjectTypes défini sur le contrôleur du widget (si défini))  */
		@Prop()
		public ObjectType!: string;

		/**  Widget sélectionné dans la bibliothèque (v-model du composant) */
		@Model('WidgetSelectedEvent', { default: () => new tWidgetLibrary() }) /** type: tWidgetLibrary, */
		public SelectedWidget!: tWidgetLibrary;

		/** Ensemble de widgets disponibles dans la bibliothèque */
		@Getter('WidgetLibraryStore/GetWidgetsLibrary')
		private widgetsLibrary: (dashboardType: DashboardTypeEnum, objectType: string) => tWidgetLibrary[];

		/** 
		 * Sélectionne un widget
		 * @param name Nom du widget à sélectionner
		 * @param addWidget Indique au composant parent que l'ajout d'un widget doit être réalisé sans démarrage de drag'n'drop
		 */
		private selectWidgetLibrary(name: string, addWidget: boolean = false) {
			console.debug("WidgetLibrary - selectWidgetLibrary - name : ", name);

			let selectedWidget: tWidgetLibrary = this.widgetsLibrary(this.DashboardType, this.ObjectType).find(x => x.Name == name);
			if (selectedWidget) {
				this.$emit('WidgetSelectedEvent', selectedWidget);

				if (addWidget)
					this.$emit('AddWidgetEvent');
				else
					this.$emit('DragStartEvent');
			}
		}

		/** Ouverture de l'info-bulle associée au widget */
		private openToolTip(widgetRef) {
			console.debug("WidgetLibrary - openToolTip(%O) - ref : %O", widgetRef, this.$refs['tooltip' + widgetRef]);

			if ('tooltip' + widgetRef in this.$refs) {
				// Ferme toutes les autres tooptip
				(this.$root as any).$emit('bv::hide::tooltip');

				// Ouvre la tooltip ciblée
				(this.$refs['tooltip' + widgetRef] as any)[0].$emit('open');
			}
		}
	}
</script>

<style scoped>
	/** Modifie le placement de la b-sidebar */
	:deep(.b-sidebar) {
		/** Indique à quelle hauteur apparait la SideBar */
		top: var(--SideBarTop) !important;
		/** Calcul de la hauteur de la SideBar */
		height: calc(var(--fullHeight) - var(--SideBarTop, 0px));
	}

	/** Item de widget */
    .WidgetLibrary {
		background: var(--gray-light);
		border: 2px dashed var(--Variant);
        cursor: grab;
    }

	/** Icon de l'item de widget */
	.WidgetIcon {
		height: 2.5rem;
		width: 2.5rem;
		padding: 0 !important;
		border-radius: 50% !important;
		border: 1px solid var(--Variant);
		background: var(--VariantLight) !important;
		color: var(--Variant) !important;
	}

	/** Item et icone de widget lorsque désactivé */
    .WidgetLibrary.disabled, .WidgetIcon.disabled {
        cursor: default !important;
        pointer-events: none !important;
		border-color: var(--gray);
		color: var(--gray-dark) !important;
    }

	/** Item de widget lorsque désactivé */
	.WidgetLibrary.disabled {
		background: var(--gray-light) !important;
	}

	/** Icone de widget lorsque désactivé */
	.WidgetIcon.disabled {
		background: var(--gray) !important;
	}
  
	/** Description de l'item de widget lorsque désactivé */
    .Description.disabled {
        color: grey;
        cursor: default;
    }
</style>